// Auto-rig marker placement UI (roadmap 3.3): a creator clicks eight
// landmarks, in order, directly on an uploaded static mesh (chin, wrists,
// elbows, knees, groin - the Mixamo-style set `autoRigJoints.ts` expects),
// and once all eight are placed `buildAutoRig` turns that into a real,
// bound `THREE.SkinnedMesh` on the canonical seven-bone skeleton. This
// panel owns marker placement and the engine-side build only; export,
// upload and `CharacterDefinition` persistence are the host's job (handed
// the built scene/mesh via `onBuilt`), same split BoneMappingPanel uses
// for `onSave`.
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { disposeProp } from '../animation/GalleryStage';
import { buildAutoRig } from '../rig/autoRigBuild';
import { LANDMARK_LABELS, LANDMARK_ORDER, type LandmarkMarkers, type Vector3Tuple } from '../rig/autoRigJoints';
import { Viewport } from '../../runtime/viewport/Viewport';

function findFirstMesh(root: THREE.Object3D): THREE.Mesh | null {
  let found: THREE.Mesh | null = null;
  root.traverse((node) => {
    if (!found && (node as THREE.Mesh).isMesh) found = node as THREE.Mesh;
  });
  return found;
}

/** Only the first mesh in traversal order gets rigged - a multi-part upload
 * (separate body/hair/clothing/eyes meshes, common for character GLBs) has
 * every other part silently dropped, so callers surface this count to warn
 * the creator rather than doing it silently. */
function countMeshes(root: THREE.Object3D): number {
  let count = 0;
  root.traverse((node) => {
    if ((node as THREE.Mesh).isMesh) count += 1;
  });
  return count;
}

const MARKER_COLOURS = ['#f2545b', '#f2a154', '#f2ce54', '#8fd15a', '#54c2f2', '#5471f2', '#a854f2', '#f254d3'];

const STYLES = {
  status: { padding: 24, fontSize: 13, color: 'var(--ss-text-2, #8b929c)' } satisfies CSSProperties,
  root: {
    display: 'flex', minHeight: 0, flex: 1,
    background: 'var(--ss-surface, #14171c)', color: 'var(--ss-text, #dfe3e8)',
  } satisfies CSSProperties,
  listColumn: {
    width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
    borderRight: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
  } satisfies CSSProperties,
  header: {
    padding: '14px 16px', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--ss-text-2, #8b929c)', borderBottom: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
  } satisfies CSSProperties,
  rows: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2 } satisfies CSSProperties,
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 6, fontSize: 12.5 } satisfies CSSProperties,
  swatch: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 } satisfies CSSProperties,
  hint: { padding: '0 16px 12px', fontSize: 11.5, lineHeight: 1.5, color: 'var(--ss-text-3, #6d747e)' } satisfies CSSProperties,
  actions: { display: 'flex', gap: 8, padding: 14, borderTop: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))' } satisfies CSSProperties,
  primaryButton: {
    flex: 1, padding: '9px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, border: 'none',
    background: 'var(--ss-accent, #6444d6)', color: 'var(--ss-accent-ink, #ffffff)', cursor: 'pointer',
  } satisfies CSSProperties,
  ghostButton: {
    padding: '9px 14px', fontSize: 12.5, fontWeight: 500, borderRadius: 8,
    border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))', background: 'transparent',
    color: 'var(--ss-text-2, #8b929c)', cursor: 'pointer',
  } satisfies CSSProperties,
  previewStage: { flex: 1, minWidth: 0, position: 'relative' } satisfies CSSProperties,
} as const;

export interface AutoRigPanelProps {
  /** Uploaded static (unrigged) GLB's download URL to place markers on. */
  sourceUrl: string;
  /** Called once the creator confirms with all eight markers placed and the
   * skeleton has been built - `scene` is export-ready (hand it straight to
   * `GLTFExporter`), `mesh` is the same `SkinnedMesh` as a convenience. */
  onBuilt: (result: { scene: THREE.Group; mesh: THREE.SkinnedMesh }) => void;
  onCancel: () => void;
}

type LoadState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; mesh: THREE.Mesh; scene: THREE.Object3D; meshCount: number };

function MarkerGizmo({ position, colour }: { position: THREE.Vector3; colour: string }) {
  return (
    <mesh position={position}>
      <sphereGeometry args={[0.025, 12, 12]} />
      <meshBasicMaterial color={colour} depthTest={false} />
    </mesh>
  );
}

/**
 * The marker-placement stage: renders the uploaded scene, and each click on
 * it places the next unplaced landmark in `LANDMARK_ORDER` (a already-full
 * marker set ignores further clicks - Undo/Reset are the only way back).
 * Renders `scene` (not `mesh` directly) so `mesh` stays nested inside it in
 * the live R3F tree - `<primitive object={mesh}>` would otherwise reparent
 * `mesh` out of `scene` (three.js's `Object3D.add` detaches a child from its
 * previous parent first), leaving it outside the tree the loader effect's
 * cleanup disposes and leaking its geometry/material every time the panel
 * closes. R3F's raycasting still bubbles the click up from whichever mesh
 * was actually hit, so `handleClick` keeps working the same either way.
 */
function PlacementStage({ mesh, scene, markers, onPlace }: {
  mesh: THREE.Mesh;
  scene: THREE.Object3D;
  markers: Partial<Record<keyof LandmarkMarkers, Vector3Tuple>>;
  onPlace: (local: Vector3Tuple) => void;
}) {
  const handleClick = useCallback((event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    const local = mesh.worldToLocal(event.point.clone());
    onPlace([local.x, local.y, local.z]);
  }, [mesh, onPlace]);

  return (
    <Viewport shadows>
      <PerspectiveCamera makeDefault fov={45} near={0.05} far={100} position={[2.4, 1.6, 3.6]} />
      <color attach="background" args={['#11141b']} />
      <hemisphereLight args={['#bcd2ff', '#202833', 1.1]} />
      <directionalLight color="#fff4e6" intensity={2.2} position={[3, 6, 4]} />
      <gridHelper args={[12, 24, '#2a3344', '#222a36']} />
      <primitive object={scene} onClick={handleClick} />
      {LANDMARK_ORDER.map((name, i) => {
        const local = markers[name];
        if (!local) return null;
        const world = mesh.localToWorld(new THREE.Vector3(...local));
        return <MarkerGizmo key={name} position={world} colour={MARKER_COLOURS[i % MARKER_COLOURS.length]!} />;
      })}
      <OrbitControls target={[0, 0.9, 0]} enableDamping />
    </Viewport>
  );
}

export function AutoRigPanel({ sourceUrl, onBuilt, onCancel }: AutoRigPanelProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [markers, setMarkers] = useState<Partial<Record<keyof LandmarkMarkers, Vector3Tuple>>>({});
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loadedScene: THREE.Object3D | null = null;
    // The one decision "is there a loaded scene this effect no longer owns,
    // and if so, dispose it" - shared by both places that can discover that
    // (see the two call sites below), rather than duplicated inline logic.
    // Idempotent (nulls `loadedScene` out after disposing) so calling it
    // twice - e.g. a late `onLoad` arrival right after cleanup already ran
    // and already found nothing to dispose - can never double-dispose.
    const disposeIfStale = () => {
      if (cancelled && loadedScene) {
        disposeProp(loadedScene);
        loadedScene = null;
      }
    };
    setState({ status: 'loading' });
    setMarkers({});
    new GLTFLoader().load(
      sourceUrl,
      (gltf) => {
        // Captured unconditionally, regardless of `cancelled` - a load that
        // completes after this effect was already cleaned up (unmount, or
        // switching auto-rig target mid-load) still produces real,
        // GPU-backed geometry/materials/textures that need disposing.
        // Checking `cancelled` before this assignment (as this used to)
        // meant a late arrival never got assigned here at all, so the
        // cleanup below - which, for an in-flight network load, has always
        // already RUN by the time this callback fires, and runs only once
        // - had nothing to dispose, and the load leaked silently. Calling
        // `disposeIfStale()` right here, immediately after assigning, is
        // what actually catches that case: the cleanup closure below only
        // ever gets a chance to dispose a load that finished BEFORE it ran.
        loadedScene = gltf.scene;
        disposeIfStale();
        if (cancelled) return;
        const mesh = findFirstMesh(gltf.scene);
        if (!mesh) {
          setState({ status: 'error', message: 'No mesh found in this GLB.' });
          return;
        }
        mesh.updateWorldMatrix(true, false);
        setState({ status: 'ready', mesh, scene: gltf.scene, meshCount: countMeshes(gltf.scene) });
      },
      undefined,
      (err) => {
        if (cancelled) return;
        setState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load the model.' });
      },
    );
    return () => {
      cancelled = true;
      disposeIfStale();
    };
  }, [sourceUrl]);

  const placedCount = LANDMARK_ORDER.filter((name) => markers[name]).length;
  const nextName = LANDMARK_ORDER[placedCount];

  const handlePlace = useCallback((local: Vector3Tuple) => {
    if (!nextName) return;
    setMarkers((prev) => ({ ...prev, [nextName]: local }));
  }, [nextName]);

  const undo = useCallback(() => {
    setMarkers((prev) => {
      if (placedCount === 0) return prev;
      const lastName = LANDMARK_ORDER[placedCount - 1]!;
      const next = { ...prev };
      delete next[lastName];
      return next;
    });
  }, [placedCount]);

  const reset = useCallback(() => setMarkers({}), []);

  const isComplete = placedCount === LANDMARK_ORDER.length;

  const build = useCallback(async () => {
    if (state.status !== 'ready' || !isComplete) return;
    setBuilding(true);
    setError(null);
    try {
      const complete = markers as LandmarkMarkers;
      const sourceMaterial = Array.isArray(state.mesh.material) ? undefined : state.mesh.material;
      // buildAutoRig is async and yields back to the browser periodically
      // (autoRigGeometry.ts) - awaiting it here (rather than the old
      // synchronous call) is what actually lets the `setBuilding(true)`
      // above paint "Building…" before the heavy geodesic search runs,
      // instead of the whole computation blocking the same tick.
      const result = sourceMaterial
        ? await buildAutoRig(state.mesh.geometry.clone(), complete, sourceMaterial)
        : await buildAutoRig(state.mesh.geometry.clone(), complete);
      onBuilt(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build the rig.');
    } finally {
      setBuilding(false);
    }
  }, [state, markers, isComplete, onBuilt]);

  const rows = useMemo(() => LANDMARK_ORDER.map((name, i) => ({
    name, label: LANDMARK_LABELS[name], colour: MARKER_COLOURS[i % MARKER_COLOURS.length]!, placed: Boolean(markers[name]),
  })), [markers]);

  if (state.status === 'loading') return <div style={STYLES.status}>Loading model…</div>;
  if (state.status === 'error') return <div style={{ ...STYLES.status, color: 'var(--ss-danger, #d0453f)' }}>{state.message}</div>;

  return (
    <div style={STYLES.root}>
      <div style={STYLES.listColumn}>
        <div style={STYLES.header}>Landmarks · {placedCount} / {LANDMARK_ORDER.length}</div>
        <div style={STYLES.hint}>
          {isComplete
            ? 'All landmarks placed. Build the rig, or Undo to adjust one.'
            : `Click on the model to place: ${LANDMARK_LABELS[nextName!]}`}
          {' '}Works best on a single connected mesh in a T/A-pose - a separate,
          disconnected piece (a detached prop, a broken seam) will bind to its
          nearest landmark by straight-line distance instead of following the surface.
          {state.meshCount > 1 && (
            <>
              {' '}This upload has {state.meshCount} separate mesh parts - only the
              first one found is placed and rigged; the rest will not appear on
              the built character.
            </>
          )}
        </div>
        <div style={STYLES.rows}>
          {rows.map((row) => (
            <div key={row.name} style={{ ...STYLES.row, opacity: row.placed ? 1 : 0.55 }}>
              <span style={{ ...STYLES.swatch, background: row.colour }} />
              <span>{row.label}</span>
              {row.placed && <span style={{ marginLeft: 'auto', color: 'var(--ss-success, #4caf6d)' }}>✓</span>}
            </div>
          ))}
        </div>
        {error && <div style={{ padding: '0 16px 8px', fontSize: 11.5, color: 'var(--ss-danger, #d0453f)' }}>{error}</div>}
        <div style={STYLES.actions}>
          <button type="button" style={STYLES.ghostButton} onClick={onCancel}>Cancel</button>
          <button type="button" style={STYLES.ghostButton} onClick={undo} disabled={placedCount === 0}>Undo</button>
          <button type="button" style={STYLES.ghostButton} onClick={reset} disabled={placedCount === 0}>Reset</button>
          <button type="button" style={STYLES.primaryButton} onClick={() => void build()} disabled={!isComplete || building}>
            {building ? 'Building…' : 'Build rig'}
          </button>
        </div>
      </div>
      <div style={STYLES.previewStage}>
        <PlacementStage mesh={state.mesh} scene={state.scene} markers={markers} onPlace={handlePlace} />
      </div>
    </div>
  );
}
